// components/AdminTopBar.jsx
import { useEffect, useState, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGear, faTrash, faUpload, faImage, faMugHot } from "@fortawesome/free-solid-svg-icons";

export default function AdminTopBar({
  eventName,
  setEventName,
  originalEventName,
  setOriginalEventName,
  handleLogout,
  onShowSettings,
  showBackButton = false,
  onBack,
  refreshTrigger = 0,
  activeStage,
  onShowBreak,
  onStartTutorial
}) {
  const [isEditingName, setIsEditingName] = useState(false);
  const [eventImgUrl, setEventImgUrl] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [logoMenuOpen, setLogoMenuOpen] = useState(false);
  const [isBreakActive, setIsBreakActive] = useState(false);
  const [breakEndTime, setBreakEndTime] = useState(null);
  const [localRefresh, setLocalRefresh] = useState(0);

  const menuRef = useRef(null);
  const gearBtnRef = useRef(null);
  const logoRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const secretId = localStorage.getItem("secretId");
    if (!secretId) return;

    (async () => {
      try {
        const res = await fetch(`https://api18081113.sayq.pl/event-settings/${secretId}`);
        const data = await res.json();
        if (!res.ok) return;
        if (data && data["event-img"]) {
          const raw = String(data["event-img"]);
          setEventImgUrl(raw.startsWith("http") ? raw : `https://eventicon.sayq.pl/${raw}`);
        } else {
          setEventImgUrl("");
        }
        if (data) {
          setIsBreakActive(!!Number(data.break_active));
          setBreakEndTime(data.break_end_time);
        }
      } catch (e) {
        console.error("Błąd pobierania logo do topbara:", e);
      }
    })();
  }, [refreshTrigger, localRefresh, activeStage]);

  useEffect(() => {
    if (!isBreakActive || !breakEndTime) return;
    const end = new Date(breakEndTime).getTime();
    const now = Date.now();
    const delay = end - now;

    if (delay > 0) {
      const timer = setTimeout(() => {
        // Active Stop from Frontend
        const sid = localStorage.getItem("secretId");
        if (sid) {
          fetch(`https://api18081113.sayq.pl/update-break`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ secretId: sid, active: false })
          }).then(() => setLocalRefresh(p => p + 1));
        }
      }, delay);
      return () => clearTimeout(timer);
    }
  }, [isBreakActive, breakEndTime]);

  const handleNameUpdate = async () => {
    if (!eventName?.trim() || eventName === originalEventName) return;
    try {
      const res = await fetch("https://api18081113.sayq.pl/update-event-name", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ secretId: localStorage.getItem("secretId"), eventName }),
      });
      if (res.ok) setOriginalEventName(eventName);
    } catch (err) {
      console.error("Błąd aktualizacji nazwy wydarzenia:", err);
    }
  };

  /* ----- LOGO HANDLERS ----- */
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoMenuOpen(false);

    const secretId = localStorage.getItem("secretId");
    if (!secretId) return;

    const uploadData = new FormData();
    uploadData.append("event-img", file);
    uploadData.append("secred-id", secretId);

    try {
      // 1. Upload file
      const res = await fetch("https://api18081113.sayq.pl/upload-event-image-base64", {
        method: "POST",
        body: uploadData
      });
      const data = await res.json();

      if (data.status === "success" || data.success) {
        const filename = data.url || data.filename || data.file;
        if (filename) {
          // 2. Update settings to persist filename
          const formData = new FormData();
          formData.append("secred-id", secretId);
          formData.append("event-img", filename);

          await fetch("https://api18081113.sayq.pl/update-event-settings", {
            method: "POST",
            body: formData
          });

          // 3. Update local state
          const newUrl = filename.startsWith("http") ? filename : `https://eventicon.sayq.pl/${filename}`;
          setEventImgUrl(newUrl);
        }
      } else {
        alert("Błąd przesyłania logo: " + (data.message || "Nieznany błąd"));
      }
    } catch (err) {
      console.error(err);
      alert("Wystąpił błąd podczas zmiany logo.");
    }
  };

  const handleDeleteLogo = async () => {
    setLogoMenuOpen(false);
    if (!window.confirm("Czy na pewno chcesz usunąć logo?")) return;

    const secretId = localStorage.getItem("secretId");
    try {
      const formData = new FormData();
      formData.append("secred-id", secretId);
      formData.append("event-img", ""); // Send empty string to clear

      const res = await fetch("https://api18081113.sayq.pl/update-event-settings", {
        method: "POST",
        body: formData
      });

      if (res.ok) {
        setEventImgUrl("");
      } else {
        alert("Nie udało się usunąć logo.");
      }
    } catch (err) {
      console.error(err);
      alert("Błąd sieci.");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") { e.preventDefault(); handleNameUpdate(); setIsEditingName(false); }
    else if (e.key === "Escape") { setEventName(originalEventName); setIsEditingName(false); }
  };

  // zamykanie menu po kliknięciu poza
  useEffect(() => {
    const off = (e) => {
      // Gear menu
      if (menuRef.current && !menuRef.current.contains(e.target) && !gearBtnRef.current?.contains(e.target)) {
        setMenuOpen(false);
      }
      // Logo menu
      if (logoRef.current && !logoRef.current.contains(e.target)) {
        setLogoMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", off);
    document.addEventListener("touchstart", off, { passive: true });
    // Escape handled separately or shared
    return () => {
      document.removeEventListener("mousedown", off);
      document.removeEventListener("touchstart", off);
    };
  }, []);

  const callSettings = () => {
    setMenuOpen(false);
    if (typeof onShowSettings === "function") onShowSettings();
    else console.warn("[AdminTopBar] onShowSettings nie został przekazany jako props.");
  };

  const callLogout = () => {
    setMenuOpen(false);
    if (typeof handleLogout === "function") handleLogout();
    else console.warn("[AdminTopBar] handleLogout nie został przekazany jako props.");
  };

  return (
    <div
      className="admin-top-bar d-flex align-items-center justify-content-between px-4 py-3 sticky-top"
      style={{
        background: 'rgba(255, 255, 255, 0.8)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderBottom: '1px solid var(--border-color)',
        zIndex: 1050,
        height: '80px',
        transition: 'all 0.3s ease'
      }}
      data-tour-id="admin-top-bar"
    >
      <div className="d-flex align-items-center gap-3">
        {showBackButton ? (
          <button
            onClick={onBack}
            className="btn btn-light bg-light rounded-pill px-4 py-2 d-flex align-items-center gap-2 shadow-sm transition-transform hover-scale fw-bold text-dark"
          >
            <FontAwesomeIcon icon={faGear} style={{ transform: 'rotate(180deg)' }} className="d-none" />
            <span>&larr; Wróć</span>
          </button>
        ) : (
          <>
            {/* LOGO SECTION with DROPDOWN */}
            <div className="position-relative" ref={logoRef}>
              <div
                onClick={() => setLogoMenuOpen(prev => !prev)}
                className="cursor-pointer transition-transform hover-scale"
                title="Kliknij, aby zarządzać logo"
                style={{ cursor: 'pointer' }}
              >
                {eventImgUrl ? (
                  <img src={eventImgUrl} alt="Logo wydarzenia" className="rounded-3 object-fit-cover shadow-sm" style={{ width: 48, height: 48 }} draggable={false} />
                ) : (
                  <div className="rounded-3 bg-light d-flex align-items-center justify-content-center text-secondary shadow-sm" style={{ width: 48, height: 48 }}>
                    <FontAwesomeIcon icon={faImage} />
                  </div>
                )}
              </div>

              {logoMenuOpen && (
                <div
                  className="position-absolute bg-white rounded-lg shadow-md border border-light py-2 px-1 overflow-hidden"
                  style={{
                    top: "calc(100% + 10px)",
                    left: "0px",
                    minWidth: 160,
                    zIndex: 9999,
                  }}
                >
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="dropdown-item px-3 py-2 text-start border-bottom hover-bg-light fw-medium text-dark d-flex align-items-center gap-2"
                  >
                    <FontAwesomeIcon icon={faUpload} className="text-secondary" /> Prześlij
                  </button>
                  {eventImgUrl && (
                    <button
                      onClick={handleDeleteLogo}
                      className="dropdown-item px-3 py-2 text-start text-danger hover-bg-light fw-medium d-flex align-items-center gap-2"
                    >
                      <FontAwesomeIcon icon={faTrash} /> Usuń
                    </button>
                  )}
                  <input
                    type="file"
                    hidden
                    ref={fileInputRef}
                    accept="image/*"
                    onChange={handleLogoUpload}
                  />
                </div>
              )}
            </div>

            {isEditingName ? (
              <input
                type="text"
                value={eventName}
                onChange={(e) => setEventName(e.target.value)}
                onBlur={() => { handleNameUpdate(); setIsEditingName(false); }}
                onKeyDown={handleKeyDown}
                autoFocus
                className="form-control fw-bold border-0 bg-light px-3 py-2 rounded-3"
                style={{ fontSize: '1.25rem', maxWidth: '400px' }}
              />
            ) : (
              <div className="d-flex align-items-center gap-2">
                <h4
                  className="m-0 fw-bold text-dark text-truncate"
                  style={{ fontSize: '1.25rem', letterSpacing: '-0.3px', color: 'var(--text-main)' }}
                >
                  {eventName || "Nazwa wydarzenia"}
                </h4>
                <button
                  onClick={() => setIsEditingName(true)}
                  className="btn btn-sm btn-light rounded-pill px-2 py-1 text-primary shadow-sm hover-lift opacity-75 hover-opacity-100 transition-standard"
                  style={{ fontSize: '0.65rem', border: '1px solid var(--border-color)' }}
                  title="Edytuj nazwę"
                >
                  EDYTUJ NAZWĘ
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* ACTION BUTTONS */}
      <div className="d-flex align-items-center gap-2">
        {/* Break Button */}
        <button
          onClick={onShowBreak}
          className={`btn d-flex align-items-center gap-2 px-3 py-2 rounded-pill shadow-md border-0 transition-standard ${isBreakActive ? 'btn-danger animate-pulse' : 'btn-light border'} fw-bold`}
          style={{ fontSize: '0.85rem' }}
        >
          <FontAwesomeIcon icon={faMugHot} />
          <span>{isBreakActive ? 'PRZERWA AKTYWNA' : 'WŁĄCZ PRZERWĘ'}</span>
        </button>

        {/* Settings Menu Button */}
        <button
          ref={gearBtnRef}
          onClick={() => setMenuOpen((p) => !p)}
          className="btn btn-light bg-light rounded-circle d-flex align-items-center justify-content-center shadow-sm transition-transform hover-scale"
          title="Menu"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          type="button"
          style={{ width: 48, height: 48, transition: 'transform 0.2s' }}
        >
          <FontAwesomeIcon icon={faGear} className="text-secondary fs-5" />
        </button>
      </div>

      {/* Dropdown pod topbarem */}
      {menuOpen && (
        <div
          ref={menuRef}
          role="menu"
          aria-label="Menu administracyjne"
          className="position-absolute bg-white shadow rounded-4 overflow-hidden border-0"
          style={{
            top: "calc(100% + 10px)", // poniżej paska
            right: "0px",
            minWidth: 200,
            zIndex: 9999,
          }}
        >
          <button
            role="menuitem"
            type="button"
            onClick={() => { setMenuOpen(false); onStartTutorial(); }}
            className="dropdown-item px-4 py-3 text-start border-bottom hover-bg-light fw-medium text-dark"
            style={{ cursor: 'pointer', transition: 'background-color 0.2s' }}
          >
            Uruchom samouczek
          </button>
          <button
            role="menuitem"
            type="button"
            onClick={callSettings}
            className="dropdown-item px-4 py-3 text-start border-bottom hover-bg-light fw-medium text-dark"
            style={{ cursor: 'pointer', transition: 'background-color 0.2s' }}
          >
            Ustawienia wydarzenia
          </button>
          <button
            role="menuitem"
            type="button"
            onClick={callLogout}
            className="dropdown-item px-4 py-3 text-start text-danger hover-bg-light fw-medium"
            style={{ cursor: 'pointer', transition: 'background-color 0.2s' }}
          >
            Wyloguj
          </button>
        </div>
      )}

      <style>{`
        .hover-scale { transition: transform 0.2s ease; }
        .hover-scale:hover { transform: translateY(-1px) scale(1.02); }
        .hover-bg-light:hover { background-color: var(--bg-soft); }
        @keyframes pulse-warning {
          0% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.4); }
          70% { box-shadow: 0 0 0 10px rgba(245, 158, 11, 0); }
          100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0); }
        }
        .pulse-warning {
          animation: pulse-warning 2s infinite;
          background-color: #f59e0b !important;
          border-color: #f59e0b !important;
        }
      `}</style>
    </div>
  );
}

/* --- mały helper do stylów pozycji w menu --- */
function itemStyle(withBorder) {
  return {
    display: "block",
    width: "100%",
    textAlign: "left",
    padding: "10px 12px",
    background: "transparent",
    border: 0,
    borderBottom: withBorder ? "1px solid #eee" : "0",
    cursor: "pointer",
    fontSize: 14,
  };
}
